import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import TeamVoicemailInbox from "@/components/TeamVoicemailInbox";
import SipCodecPicker, { DEFAULT_SIP_CODECS, normalizeSipCodecs } from "@/components/SipCodecPicker";

const emptyUser = () => ({
  name: "", email: "", password: "", status: "active", group_ids: [], app_type: "agent",
  voicemail_enabled: true,
  phone_type: "sip",
  allowed_codecs: [...DEFAULT_SIP_CODECS],
  allow_direct_call: "no",
  my_call_back: "no",
  display_stats: "no",
  agent_level: "1",
});
const emptyPhone = () => ({
  sip_username: "", sip_password: "", voicemail_enabled: true,
  voicemail_mailbox: "", voicemail_pin: "", phone_type: "sip",
  allowed_codecs: [...DEFAULT_SIP_CODECS],
});
const emptyGroup = () => ({ name: "", description: "", status: "active" });

export default function CustomerTeam() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userDlg, setUserDlg] = useState(null); // null | 'new' | user
  const [groupDlg, setGroupDlg] = useState(null);
  const [userForm, setUserForm] = useState(emptyUser());
  const [groupForm, setGroupForm] = useState(emptyGroup());
  const [saving, setSaving] = useState(false);
  const [phoneDlg, setPhoneDlg] = useState(null); // user row
  const [phoneForm, setPhoneForm] = useState(emptyPhone());
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [vmUser, setVmUser] = useState(null);
  const [webrtcAvailable, setWebrtcAvailable] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const [u, g, rtc] = await Promise.all([
        api.get("/customer-team/users", { params }),
        api.get("/customer-team/groups"),
        api.get("/customer-team/webrtc-available").catch(() => ({ data: { webrtc_available: false } })),
      ]);
      setUsers(u.data || []);
      setGroups(g.data || []);
      setWebrtcAvailable(!!rtc.data?.webrtc_available);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openNewUser = () => { setUserForm(emptyUser()); setUserDlg("new"); };
  const openEditUser = (u) => {
    const ptype = u.phone?.phone_type === "web" ? "web" : "sip";
    setUserForm({
      name: u.name || "", email: u.email || "", password: "",
      status: u.status === "suspended" ? "suspended" : "active",
      group_ids: u.group_ids || [],
      app_type: u.app_type === "manager" ? "manager" : "agent",
      voicemail_enabled: u.phone?.voicemail_enabled !== false,
      phone_type: webrtcAvailable ? ptype : "sip",
      allow_direct_call: u.allow_direct_call === true ? "yes" : "no",
      my_call_back: u.my_call_back === true ? "yes" : "no",
      display_stats: u.display_stats === true ? "yes" : "no",
      agent_level: String([1, 2, 3, 4, 5].includes(u.agent_level) ? u.agent_level : 1),
    });
    setUserDlg(u);
  };

  const saveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error("Name and email are required"); return;
    }
    if (userDlg === "new" && userForm.password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    if (userDlg !== "new" && userForm.password && userForm.password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    setSaving(true);
    try {
      const allowDirect = userForm.allow_direct_call === "yes";
      const myCallBack = userForm.my_call_back === "yes";
      const displayStats = userForm.display_stats === "yes";
      const agentLevel = Number(userForm.agent_level) || 1;
      const phoneType = webrtcAvailable && userForm.phone_type === "web" ? "web" : "sip";
      if (userDlg === "new") {
        const { data } = await api.post("/customer-team/users", {
          ...userForm,
          phone_type: phoneType,
          allowed_codecs: normalizeSipCodecs(userForm.allowed_codecs),
          allow_direct_call: allowDirect,
          my_call_back: myCallBack,
          display_stats: displayStats,
          agent_level: agentLevel,
        });
        const sip = data?.phone?.sip_username;
        toast.success(sip
          ? `User created · SIP ${sip}`
          : "User created");
      } else {
        const body = {
          name: userForm.name, email: userForm.email,
          status: userForm.status, group_ids: userForm.group_ids,
          app_type: userForm.app_type,
          allow_direct_call: allowDirect,
          my_call_back: myCallBack,
          display_stats: displayStats,
          agent_level: agentLevel,
        };
        if (webrtcAvailable) body.phone_type = phoneType;
        if (userForm.password) body.password = userForm.password;
        await api.patch(`/customer-team/users/${userDlg.id}`, body);
        toast.success("User updated");
      }
      setUserDlg(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/customer-team/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const openPhone = async (u) => {
    setPhoneDlg(u);
    setPhoneForm({
      ...emptyPhone(),
      sip_username: u.phone?.sip_username || "",
      voicemail_enabled: !!u.phone?.voicemail_enabled,
      phone_type: u.phone?.phone_type === "web" ? "web" : "sip",
      allowed_codecs: normalizeSipCodecs(u.phone?.allowed_codecs),
    });
    setPhoneLoading(true);
    try {
      const { data } = await api.get(`/customer-team/users/${u.id}/phone`);
      setPhoneForm({
        sip_username: data.sip_username || "",
        sip_password: data.sip_password || "",
        voicemail_enabled: !!data.voicemail_enabled,
        voicemail_mailbox: data.voicemail_mailbox || data.sip_username || "",
        voicemail_pin: data.voicemail_pin || "",
        phone_type: data.phone_type === "web" ? "web" : "sip",
        allowed_codecs: normalizeSipCodecs(data.allowed_codecs),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setPhoneDlg(null);
    } finally {
      setPhoneLoading(false);
    }
  };

  const savePhone = async () => {
    if (!phoneDlg) return;
    if (!phoneForm.sip_username.trim()) {
      toast.error("SIP username is required"); return;
    }
    setSavingPhone(true);
    try {
      const body = {
        sip_username: phoneForm.sip_username.trim(),
        voicemail_enabled: !!phoneForm.voicemail_enabled,
        allowed_codecs: normalizeSipCodecs(phoneForm.allowed_codecs),
      };
      if (phoneForm.sip_password.trim()) body.sip_password = phoneForm.sip_password.trim();
      if (phoneForm.voicemail_pin.trim()) body.voicemail_pin = phoneForm.voicemail_pin.trim();
      if (webrtcAvailable) {
        body.phone_type = phoneForm.phone_type === "web" ? "web" : "sip";
      }
      await api.patch(`/customer-team/users/${phoneDlg.id}/phone`, body);
      toast.success("Phone credentials updated");
      setPhoneDlg(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSavingPhone(false);
    }
  };

  const openNewGroup = () => { setGroupForm(emptyGroup()); setGroupDlg("new"); };
  const openEditGroup = (g) => {
    setGroupForm({ name: g.name || "", description: g.description || "", status: g.status || "active" });
    setGroupDlg(g);
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error("Group name is required"); return; }
    setSaving(true);
    try {
      if (groupDlg === "new") {
        await api.post("/customer-team/groups", groupForm);
        toast.success("Group created");
      } else {
        await api.patch(`/customer-team/groups/${groupDlg.id}`, groupForm);
        toast.success("Group updated");
      }
      setGroupDlg(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group “${g.name}”? Members will be unassigned.`)) return;
    try {
      await api.delete(`/customer-team/groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const toggleGroup = (gid, checked) => {
    setUserForm((f) => ({
      ...f,
      group_ids: checked
        ? [...f.group_ids, gid]
        : f.group_ids.filter((x) => x !== gid),
    }));
  };

  return (
    <div className="space-y-6" data-testid="customer-team-page">
      <PageHeader
        title="Users & Groups"
        subtitle="Manage logins and groups for your customer account"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="team-tab-users">Users</TabsTrigger>
          <TabsTrigger value="groups" data-testid="team-tab-groups">Groups</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              <div className="relative">
                <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input className="pl-8 w-56" placeholder="Search users…" value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid="team-user-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="team-user-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="rounded-lg" onClick={openNewUser} data-testid="team-add-user">
              <Plus className="w-4 h-4 mr-1.5" /> Add user
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : users.length === 0 ? (
            <EmptyState title="No users" description="Add a user login for this account." />
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>App</TableHead>
                    <TableHead>Direct call</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Groups</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`team-user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell className="text-xs capitalize text-muted-foreground" data-testid={`team-user-app-${u.id}`}>
                        {u.app_type || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-testid={`team-user-direct-call-${u.id}`}>
                        {u.allow_direct_call === true ? "Yes" : "No"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-testid={`team-user-level-${u.id}`}>
                        {u.agent_level || 1}
                      </TableCell>
                      <TableCell>
                        {u.phone?.sip_username ? (
                          <span className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              className="font-mono text-sm text-primary hover:underline"
                              onClick={() => openPhone(u)}
                              data-testid={`team-user-phone-${u.id}`}
                              title="Edit SIP credentials"
                            >
                              {u.phone.sip_username}
                              {u.phone.phone_type === "web" ? (
                                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-700">Web</span>
                              ) : null}
                              {u.phone.voicemail_enabled ? (
                                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">VM</span>
                              ) : null}
                            </button>
                            {u.voicemail_unread > 0 ? (
                              <button
                                type="button"
                                className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-sky-600 text-white text-[11px] font-semibold leading-5"
                                onClick={() => setVmUser(u)}
                                title={`${u.voicemail_unread} new voicemail${u.voicemail_unread === 1 ? "" : "s"} — listen or delete`}
                                aria-label={`${u.voicemail_unread} new voicemails`}
                                data-testid={`team-user-vm-count-${u.id}`}
                              >
                                {u.voicemail_unread}
                              </button>
                            ) : null}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-sm text-muted-foreground hover:text-primary hover:underline"
                            onClick={() => openPhone(u)}
                            data-testid={`team-user-phone-${u.id}`}
                          >
                            Create phone
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {(u.groups || []).map((g) => g.name).join(", ") || "—"}
                      </TableCell>
                      <TableCell><StatusPill status={u.status} /></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} data-testid={`team-edit-user-${u.id}`}>
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        {!u.is_primary && (
                          <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} data-testid={`team-del-user-${u.id}`}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="groups" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button className="rounded-lg" onClick={openNewGroup} data-testid="team-add-group">
              <Plus className="w-4 h-4 mr-1.5" /> Add group
            </Button>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : groups.length === 0 ? (
            <EmptyState title="No groups" description="Create a group to organize users." />
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{g.description || "—"}</TableCell>
                      <TableCell>{g.member_count ?? 0}</TableCell>
                      <TableCell><StatusPill status={g.status} /></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditGroup(g)}>
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteGroup(g)}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!userDlg} onOpenChange={(v) => !v && setUserDlg(null)}>
        <DialogContent
          data-testid="team-user-dialog"
          className="max-h-[min(90vh,720px)] flex flex-col gap-3 overflow-hidden p-6"
        >
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle>{userDlg === "new" ? "Add user" : "Edit user"}</DialogTitle>
            <DialogDescription>
              {userDlg === "new"
                ? "A SIP phone and voicemail box are created automatically and linked to this user."
                : "Update login details and App type."}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-3 py-1 pr-1">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="team-user-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="team-user-email" /></div>
            <div className="space-y-1.5"><Label>Password {userDlg !== "new" ? "(leave blank to keep)" : ""}</Label>
              <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="team-user-password" autoComplete="new-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>App</Label>
                <Select value={userForm.app_type} onValueChange={(v) => setUserForm({ ...userForm, app_type: v })}>
                  <SelectTrigger data-testid="team-user-app-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={userForm.status} onValueChange={(v) => setUserForm({ ...userForm, status: v })}>
                  <SelectTrigger data-testid="team-user-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Allow direct call</Label>
                <Select
                  value={userForm.allow_direct_call}
                  onValueChange={(v) => setUserForm({ ...userForm, allow_direct_call: v })}
                >
                  <SelectTrigger data-testid="team-user-allow-direct-call"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Only agents allowed to dial direct calls can dial using a campaign prefix.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Agent level</Label>
                <Select
                  value={userForm.agent_level}
                  onValueChange={(v) => setUserForm({ ...userForm, agent_level: v })}
                >
                  <SelectTrigger data-testid="team-user-agent-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 — lowest</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5 — highest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>My Call Back</Label>
              <Select
                value={userForm.my_call_back}
                onValueChange={(v) => setUserForm({ ...userForm, my_call_back: v })}
              >
                <SelectTrigger data-testid="team-user-my-call-back"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Yes lets this agent schedule private callbacks only they can see and be notified for.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Display Stats</Label>
              <Select
                value={userForm.display_stats}
                onValueChange={(v) => setUserForm({ ...userForm, display_stats: v })}
              >
                <SelectTrigger data-testid="team-user-display-stats"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
              {userForm.display_stats === "yes" ? (
                <p className="text-[11px] text-muted-foreground leading-snug" data-testid="team-user-display-stats-hint">
                  Yes lets this agent view their own current-day stats on the agent screen
                  (total calls, talk time, pause time, and dispositions).
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Default No — agent screen hides personal day stats.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Groups</Label>
              <div className="rounded-md border p-2.5 max-h-36 overflow-y-auto space-y-1.5">
                {groups.filter((g) => g.status === "active").map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={userForm.group_ids.includes(g.id)}
                      onCheckedChange={(v) => toggleGroup(g.id, !!v)} />
                    {g.name}
                  </label>
                ))}
                {groups.filter((g) => g.status === "active").length === 0 && (
                  <p className="text-xs text-muted-foreground">No groups yet.</p>
                )}
              </div>
            </div>
            {webrtcAvailable && (
              <div className="space-y-1.5" data-testid="team-user-phone-type-wrap">
                <Label>Phone type</Label>
                <Select
                  value={userForm.phone_type === "web" ? "web" : "sip"}
                  onValueChange={(v) => setUserForm({ ...userForm, phone_type: v })}
                >
                  <SelectTrigger data-testid="team-user-phone-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sip">SIP phone</SelectItem>
                    <SelectItem value="web">Web Phone</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {userForm.phone_type === "web"
                    ? "Agent screen shows an in-browser Webphone (WebRTC)."
                    : "Agent uses a SIP softphone / desk phone (default)."}
                </p>
              </div>
            )}
            {userDlg === "new" && (
              <SipCodecPicker
                value={userForm.allowed_codecs}
                onChange={(allowed_codecs) => setUserForm({ ...userForm, allowed_codecs })}
                testid="team-user-codecs"
                description="Codecs for this user's SIP phone. Select one or all."
              />
            )}
            {userDlg === "new" && (
              <label className="flex items-center gap-2 text-sm pt-1">
                <Checkbox
                  checked={!!userForm.voicemail_enabled}
                  onCheckedChange={(v) => setUserForm({ ...userForm, voicemail_enabled: !!v })}
                  data-testid="team-user-voicemail"
                />
                Voicemail enabled
              </label>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t border-border pt-3 mt-0">
            <Button variant="outline" className="rounded-lg" onClick={() => setUserDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={saveUser} disabled={saving} data-testid="team-user-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!phoneDlg} onOpenChange={(v) => !v && setPhoneDlg(null)}>
        <DialogContent data-testid="team-phone-dialog">
          <DialogHeader>
            <DialogTitle>Phone — {phoneDlg?.name || phoneDlg?.email}</DialogTitle>
            <DialogDescription>
              Softphone Domain / Realm: use your assigned OpenSIPS host (e.g. 157.90.96.61). Port 5060 UDP/TCP.
            </DialogDescription>
          </DialogHeader>
          {phoneLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading credentials…</p>
          ) : (
            <div className="space-y-3 py-1">
              {webrtcAvailable && (
                <div className="space-y-1.5" data-testid="team-phone-type-wrap">
                  <Label>Phone type</Label>
                  <Select
                    value={phoneForm.phone_type === "web" ? "web" : "sip"}
                    onValueChange={(v) => setPhoneForm({ ...phoneForm, phone_type: v })}
                  >
                    <SelectTrigger data-testid="team-phone-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sip">SIP phone</SelectItem>
                      <SelectItem value="web">Web Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>SIP username</Label>
                <Input
                  value={phoneForm.sip_username}
                  onChange={(e) => setPhoneForm({ ...phoneForm, sip_username: e.target.value })}
                  className="font-mono"
                  data-testid="team-phone-sip-username"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label>SIP password</Label>
                <Input
                  type="text"
                  value={phoneForm.sip_password}
                  onChange={(e) => setPhoneForm({ ...phoneForm, sip_password: e.target.value })}
                  className="font-mono"
                  data-testid="team-phone-sip-password"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Softphone: User name + Authorization user name = SIP username above; Domain = OpenSIPS IP.
                </p>
              </div>
              <SipCodecPicker
                value={phoneForm.allowed_codecs}
                onChange={(allowed_codecs) => setPhoneForm({ ...phoneForm, allowed_codecs })}
                testid="team-phone-codecs"
                description="Codecs for this SIP phone. Select one or all."
              />
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!phoneForm.voicemail_enabled}
                  onCheckedChange={(v) => setPhoneForm({ ...phoneForm, voicemail_enabled: !!v })}
                  data-testid="team-phone-voicemail"
                />
                Voicemail enabled
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Mailbox</Label>
                  <Input
                    value={phoneForm.voicemail_mailbox}
                    readOnly
                    className="font-mono bg-muted"
                    data-testid="team-phone-vm-mailbox"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>PIN</Label>
                  <Input
                    value={phoneForm.voicemail_pin}
                    onChange={(e) => setPhoneForm({ ...phoneForm, voicemail_pin: e.target.value })}
                    className="font-mono"
                    maxLength={4}
                    data-testid="team-phone-vm-pin"
                    autoComplete="off"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, inbound calls to this user go to voicemail if they are busy, unavailable, or do not answer before the ring times out.
                Dial #100# from this softphone and enter the PIN to listen to messages.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setPhoneDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={savePhone} disabled={savingPhone || phoneLoading} data-testid="team-phone-save">
              {savingPhone ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!groupDlg} onOpenChange={(v) => !v && setGroupDlg(null)}>
        <DialogContent data-testid="team-group-dialog">
          <DialogHeader>
            <DialogTitle>{groupDlg === "new" ? "Add group" : "Edit group"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} data-testid="team-group-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Input value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={groupForm.status} onValueChange={(v) => setGroupForm({ ...groupForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setGroupDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={saveGroup} disabled={saving} data-testid="team-group-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {vmUser ? (
        <TeamVoicemailInbox
          userRow={vmUser}
          onClose={() => setVmUser(null)}
          onChanged={() => load({ silent: true })}
        />
      ) : null}
    </div>
  );
}
